import React from 'react';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import Icon from '@mdi/react';
import { mdiLoading, mdiPlay } from '@mdi/js';
import { useMutation } from 'react-query';
import axios from 'axios';
import { extractApiError } from '@/lib/apiError';
import { ApplianceDTO } from '@webip/shared/src/dto/appliance';
import { DiagnosticRequestDTO, DiagnosticResultDTO, DiagnosticToolDTO } from '@webip/shared/src/dto/diagnostics';

type Props = {
    appliance: ApplianceDTO | null;
    onClose: () => void;
};

const TOOL_OPTIONS: { id: DiagnosticToolDTO; label: string }[] = [
    { id: 'ping', label: 'Ping' },
    { id: 'traceroute', label: 'Traceroute' },
    { id: 'fetch', label: 'Fetch (curl)' },
];

const TARGET_PLACEHOLDER: Record<DiagnosticToolDTO, string> = {
    ping: '8.8.8.8 ou host.exemplo.com',
    traceroute: '8.8.8.8 ou host.exemplo.com',
    fetch: 'https://exemplo.com',
};

const DiagnosticsModal = ({ appliance, onClose }: Props) => {
    const [tool, setTool] = React.useState<DiagnosticToolDTO>('ping');
    const [target, setTarget] = React.useState('');
    const [srcAddress, setSrcAddress] = React.useState('');
    const [count, setCount] = React.useState('4');
    const [result, setResult] = React.useState<DiagnosticResultDTO | null>(null);
    const [erro, setErro] = React.useState<string | null>(null);

    // Reinicia o estado quando a appliance muda (abre/fecha).
    const applianceId = appliance?.id;
    React.useEffect(() => {
        setResult(null);
        setErro(null);
    }, [applianceId]);

    const mutation = useMutation<DiagnosticResultDTO, unknown, DiagnosticRequestDTO>(
        async (payload) => {
            const res = await axios.post<DiagnosticResultDTO>(
                `/api/appliance/${applianceId}/diagnostics`,
                payload,
                { withCredentials: true },
            );
            return res.data;
        },
        {
            onSuccess: (data) => {
                setResult(data);
                setErro(data.ok ? null : data.error ?? 'Falha ao executar diagnóstico');
            },
            onError: (err) => {
                const { message } = extractApiError(err, 'Erro ao executar diagnóstico');
                setResult(null);
                setErro(message);
            },
        },
    );

    const handleRun = () => {
        if (!target.trim() || mutation.isLoading) return;
        setErro(null);
        setResult(null);
        mutation.mutate({
            tool,
            target: target.trim(),
            srcAddress: srcAddress.trim() || undefined,
            count: tool === 'fetch' ? undefined : Number(count) || undefined,
        });
    };

    const loading = mutation.isLoading;

    return (
        <Modal
            show={appliance != null}
            title={`Diagnóstico de rede${appliance ? ` — ${appliance.nome}` : ''}`}
            onClose={onClose}
            hideControls
            size="lg"
        >
            <VBox style={{ gap: '16px' }}>
                <HBox style={{ gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ minWidth: '150px' }}>
                        <CampoSeletor
                            label="Ferramenta"
                            value={tool}
                            onChange={(v) => {
                                setTool((v as DiagnosticToolDTO) ?? 'ping');
                                setResult(null);
                                setErro(null);
                            }}
                            options={TOOL_OPTIONS}
                            noSearch
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: '220px' }}>
                        <CampoTexto
                            label={tool === 'fetch' ? 'URL' : 'Destino'}
                            value={target}
                            onChange={setTarget}
                            placeholder={TARGET_PLACEHOLDER[tool]}
                        />
                    </div>
                </HBox>

                <HBox style={{ gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                        <CampoTexto
                            label="IP de origem (opcional)"
                            value={srcAddress}
                            onChange={setSrcAddress}
                            placeholder="ex.: 192.0.2.1 — testa um link/rota específico"
                        />
                    </div>
                    {tool !== 'fetch' && (
                        <div style={{ width: '110px' }}>
                            <CampoTexto
                                label="Pacotes"
                                value={count}
                                onChange={(v) => {
                                    const digits = v.replace(/[^0-9]/g, '');
                                    if (!digits) return setCount('');
                                    setCount(String(Math.min(Number(digits), 10)));
                                }}
                                placeholder="4"
                            />
                        </div>
                    )}
                    <Botao
                        variant="primary"
                        icon={loading ? mdiLoading : mdiPlay}
                        iconProps={loading ? { spin: true } : undefined}
                        onClick={handleRun}
                        disabled={loading || !target.trim()}
                    >
                        {loading ? 'Executando…' : 'Executar'}
                    </Botao>
                </HBox>

                {erro && (
                    <p
                        style={{
                            color: 'var(--tc-color-red-700)',
                            background: 'var(--tc-color-red-50)',
                            borderRadius: '6px',
                            padding: '12px',
                            fontSize: '13px',
                            margin: 0,
                        }}
                    >
                        <strong>Erro:</strong> {erro}
                    </p>
                )}

                {loading && (
                    <HBox style={{ justifyContent: 'center', padding: '24px' }}>
                        <Icon path={mdiLoading} size={1.5} spin />
                    </HBox>
                )}

                {!loading && result?.ok && (
                    <VBox style={{ gap: '8px' }}>
                        {result.summary && (
                            <span style={{ fontSize: '13px', color: 'var(--tc-color-gray-600)' }}>
                                {result.summary}
                            </span>
                        )}
                        <pre
                            style={{
                                background: 'var(--tc-color-gray-900, #111827)',
                                color: 'var(--tc-color-gray-100, #f3f4f6)',
                                borderRadius: '8px',
                                padding: '12px 14px',
                                fontFamily: 'monospace',
                                fontSize: '12.5px',
                                lineHeight: '1.6',
                                margin: 0,
                                maxHeight: '380px',
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                            }}
                        >
                            {result.output.length ? result.output.join('\n') : '(sem saída)'}
                        </pre>
                    </VBox>
                )}
            </VBox>
        </Modal>
    );
};

export default DiagnosticsModal;
